import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  onBecomeObserved,
  runInAction,
} from "mobx";
import type { AppSubStoreArgs } from "@/store/types";
import { AppSubStore } from "@/store/types";
import type {
  GoogleCalendarEntry,
  GoogleCalendarEvent,
  GoogleCalendarSettings,
} from "@/domains/calendar/google/types";
import type {
  GoogleCalendarEntrySchema,
  GoogleCalendarSettingsResponse,
  ConnectedCalendarEventRecord,
  ICalendarGoogleStore,
} from "@/store/calendar/types";
import { api } from "@/modules/api";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { localStorageModule } from "@/modules/local-storage";

export class AppStoreCalendarGoogleStore extends AppSubStore implements ICalendarGoogleStore {
  accounts: ObservableMap<string, GoogleCalendarSettings> = new ObservableMap<
    string,
    GoogleCalendarSettings
  >();
  upcomingMeetings: GoogleCalendarEvent[] = [];

  private ready = false;
  private loading = false;
  private connecting = false;
  private disconnecting = false;
  private updatingCalendars = false;

  constructor(args: AppSubStoreArgs) {
    super(args);

    makeObservable<
      AppStoreCalendarGoogleStore,
      | "ready"
      | "loading"
      | "connecting"
      | "disconnecting"
      | "updatingCalendars"
      | "mapSettingsFromResponse"
      | "setUpcomingMeetingsFromEvents"
      | "isFetchingUpcomingMeetings"
    >(this, {
      accounts: observable,
      accountsList: computed,
      upcomingMeetings: observable,
      ready: observable,
      loading: observable,
      connecting: observable,
      disconnecting: observable,
      updatingCalendars: observable,
      isReady: computed,
      isLoading: computed,
      isConnecting: computed,
      isDisconnecting: computed,
      isUpdatingCalendars: computed,
      isConnected: computed,
      isFetchingUpcomingMeetings: computed,
      loadSettings: action,
      connect: action,
      completeConnection: action,
      disconnect: action,
      updateCalendars: action,
      toggleCalendar: action,
      refreshUpcomingMeetings: action,
      mapSettingsFromResponse: false,
      setUpcomingMeetingsFromEvents: action,
      resetState: action,
    });

    onBecomeObserved(this, "isConnected", () => {
      if (!this.isReady) void this.loadSettings();
    });
  }

  get isReady() {
    return this.ready;
  }

  get isLoading() {
    return this.loading;
  }

  get isConnecting() {
    return this.connecting;
  }

  get isDisconnecting() {
    return this.disconnecting;
  }

  get isUpdatingCalendars() {
    return this.updatingCalendars;
  }

  get isFetchingUpcomingMeetings() {
    return this.store.calendar.isFetchingUpcomingMeetings;
  }

  get accountsList() {
    return Array.from(this.accounts.values());
  }

  get isConnected() {
    return this.accountsList.some(account => account.isConnected);
  }

  async loadSettings() {
    if (this.loading) return;

    runInAction(() => {
      this.loading = true;
    });

    try {
      const response = await api.get("/v2/integrations/calendar/google/settings", {});
      const data = response.data;
      if (!data) {
        throw new Error("Missing Google Calendar settings payload.");
      }

      // Load all accounts into the store
      const accounts = data.accounts ?? {};
      runInAction(() => {
        this.accounts = new ObservableMap<string, GoogleCalendarSettings>();
        for (const [subjectId, accountSettings] of Object.entries(accounts)) {
          if (accountSettings) {
            this.accounts.set(subjectId, this.mapSettingsFromResponse(accountSettings));
          }
        }
      });

      if (this.accounts.size > 0) {
        await this.store.calendar.fetchUpcomingMeetingsFromStore();
      }
      runInAction(() => {
        this.ready = true;
      });
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarGoogleStore] Failed to load settings",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async refreshUpcomingMeetings() {
    await this.store.calendar.refreshUpcomingMeetings();
  }

  async connect() {
    if (this.connecting) return;

    runInAction(() => {
      this.connecting = true;
    });

    try {
      const redirectUri = windowModule.getGoogleCalendarOAuthRedirectUrl();
      const response = await api.get("/v2/integrations/calendar/google/connect", {
        params: {
          query: {
            redirect_uri: redirectUri,
          },
        },
      });

      const data = response.data;
      if (!data) {
        throw new Error("Missing Google Calendar connect payload.");
      }

      localStorageModule.writeGoogleCalendarOAuthState(data.state);
      await windowModule.navigateToExternal({
        url: new URL(data.authorization_url),
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreCalendarGoogleStore] Failed to start Google Calendar connect flow",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Failed to start Google Calendar connection. Please try again.",
      });
    } finally {
      runInAction(() => {
        this.connecting = false;
      });
    }
  }

  async completeConnection({
    code,
    state,
    redirectUri,
  }: {
    code: string;
    state: string | null;
    redirectUri: string;
  }) {
    const storedState = localStorageModule.readGoogleCalendarOAuthState();
    if (!storedState || storedState !== state) {
      throw new Error("OAuth state mismatch.");
    }

    try {
      const response = await api.post("/v2/integrations/calendar/google/oauth/callback", {
        body: {
          code,
          redirect_uri: redirectUri,
          state,
        },
      });

      const data = response.data;
      if (!data) {
        throw new Error("Missing Google Calendar OAuth callback payload.");
      }

      const subjectId = data.google_subject_id;
      if (!subjectId) {
        throw new Error("Missing google_subject_id in Google Calendar OAuth callback response.");
      }

      runInAction(() => {
        this.accounts.set(subjectId, this.mapSettingsFromResponse(data));
      });
      await this.refreshUpcomingMeetings();
      toastModule.triggerToast({
        content: "Google Calendar connected",
      });

      return { subjectId };
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarGoogleStore] Failed to complete Google Calendar OAuth callback",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
      throw unknownError;
    } finally {
      localStorageModule.clearGoogleCalendarOAuthState();
    }
  }

  async disconnect(subjectId: string) {
    if (this.disconnecting) return;
    if (!subjectId) {
      logger.warn({
        message: "[AppStoreCalendarGoogleStore] Cannot disconnect: no subject_id",
      });
      return;
    }

    runInAction(() => {
      this.disconnecting = true;
    });

    try {
      const response = await api.post("/v2/integrations/calendar/google/disconnect", {
        params: {
          query: {
            subject_id: subjectId,
          },
        },
      });
      const data = response.data;
      if (!data) {
        throw new Error("Missing Google Calendar disconnect payload.");
      }

      // Update or remove the account from the store
      if (data.is_connected) {
        runInAction(() => {
          this.accounts.set(subjectId, this.mapSettingsFromResponse(data));
        });
      } else {
        runInAction(() => {
          this.accounts.delete(subjectId);
        });
      }

      // Clear upcoming meetings if no accounts are connected
      if (!this.isConnected) {
        runInAction(() => {
          this.upcomingMeetings = [];
        });
      }

      toastModule.triggerToast({
        content: "Google Calendar disconnected",
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreCalendarGoogleStore] Failed to disconnect Google Calendar",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Failed to disconnect Google Calendar. Please try again.",
      });
    } finally {
      runInAction(() => {
        this.disconnecting = false;
      });
    }
  }

  async updateCalendars(subjectId: string, calendars: GoogleCalendarEntry[]) {
    if (this.updatingCalendars) return;

    const account = this.accounts.get(subjectId);
    if (!account) {
      throw new Error(`Cannot update calendars: account with subject_id ${subjectId} not found`);
    }

    const previousCalendars = account.calendars.map(calendar => ({ ...calendar }));

    runInAction(() => {
      this.updatingCalendars = true;
    });

    try {
      const enabledCalendarIds = calendars
        .filter(calendar => calendar.isEnabled)
        .map(calendar => calendar.id);

      const response = await api.post("/v2/integrations/calendar/google/settings", {
        body: {
          enabled_calendar_ids: enabledCalendarIds,
          subject_id: subjectId,
        },
      });

      const data = response.data;
      if (!data) {
        throw new Error("Missing Google Calendar update settings payload.");
      }

      runInAction(() => {
        this.accounts.set(subjectId, this.mapSettingsFromResponse(data));
      });

      if (this.isConnected) {
        await this.store.calendar.fetchUpcomingMeetingsFromStore();
      } else {
        runInAction(() => {
          this.upcomingMeetings = [];
        });
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreCalendarGoogleStore] Failed to update calendar settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        const account = this.accounts.get(subjectId);
        if (account) {
          this.accounts.set(subjectId, {
            ...account,
            calendars: previousCalendars,
          });
        }
      });

      toastModule.triggerToast({
        content: "Failed to update calendars. Please try again.",
      });
    } finally {
      runInAction(() => {
        this.updatingCalendars = false;
      });
    }
  }

  async toggleCalendar({
    subjectId,
    calendarId,
    isEnabled,
  }: {
    subjectId: string;
    calendarId: string;
    isEnabled: boolean;
  }) {
    const account = this.accounts.get(subjectId);
    if (!account) return;

    const updatedCalendars = account.calendars.map(calendar =>
      calendar.id === calendarId ? { ...calendar, isEnabled } : calendar
    );

    await this.updateCalendars(subjectId, updatedCalendars);
  }

  resetState() {
    this.accounts.clear();
    this.upcomingMeetings = [];
    this.ready = false;
    this.loading = false;
    this.connecting = false;
    this.disconnecting = false;
    this.updatingCalendars = false;
  }

  private mapSettingsFromResponse(
    response: GoogleCalendarSettingsResponse
  ): GoogleCalendarSettings {
    return {
      isConnected: response.is_connected ?? false,
      calendars: (response.calendars ?? []).map(mapCalendarEntryFromResponse),
      googleAccountEmail: response.google_account_email ?? null,
      googleSubjectId: response.google_subject_id ?? null,
      connectedAt: response.connected_at ?? null,
      revokedAt: response.revoked_at ?? null,
      lastSyncedAt: response.last_synced_at ?? null,
    };
  }

  setUpcomingMeetingsFromEvents(events: ConnectedCalendarEventRecord[]) {
    if (!events || events.length === 0) {
      this.upcomingMeetings = [];
      return;
    }

    this.upcomingMeetings = events.map(event => {
      const value = event.value ?? {};
      const summary = typeof value.summary === "string" ? value.summary : null;
      const htmlLink = typeof value.html_link === "string" ? value.html_link : null;
      const meetLink = typeof value.meet_link === "string" ? value.meet_link : null;
      const location = typeof value.location === "string" ? value.location : null;
      return {
        calendarId: event.connected_calendar_id,
        eventId: event.external_event_id,
        summary,
        startAt: event.start_at,
        endAt: event.end_at,
        htmlLink,
        meetLink,
        location,
      };
    });
  }
}

export const mapCalendarEntryFromResponse = (
  entry: GoogleCalendarEntrySchema
): GoogleCalendarEntry => {
  return {
    id: entry.id,
    isPrimary: entry.is_primary ?? false,
    summary: entry.summary ?? null,
    timeZone: entry.time_zone ?? null,
    accessRole: entry.access_role ?? null,
    isEnabled: Boolean(entry.config?.is_enabled),
  };
};
